<script setup>
/**
 * 交易所切换器。
 *
 * 放在顶栏而不是系统页：交易所是**全局上下文**，界面上每一个数字都属于
 * 某一家交易所。当前是哪家必须常驻可见，否则看着 A 家的盘口对 B 家下判断。
 *
 * 切换有三道闸门，前两道在前端拦（拦不住的后端还会再拦一次）：
 *   1. 锁定态不许切 —— 否则换一家交易所就等于绕开"必须人工解除"的规则。
 *   2. 运行中先确认 —— 切换会中断当前盯盘。
 *   3. 有活动挂单不许切 —— 后端判定，切走后那些单子仍在盘上却没人看。
 */
import { computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useConsoleStore } from '@/stores/console'
import { usePlanStore } from '@/stores/plan'
import { useRiskStore } from '@/stores/risk'
import { stateLabel } from '@/utils/rules'

const c = useConsoleStore()
const plan = usePlanStore()
const risk = useRiskStore()

onMounted(() => c.loadExchanges())

const items = computed(() => c.exchanges)
const label = computed(() => c.exchangeLabel)

/* 即使只有一家可用也保持可展开。
 *
 * 一度想在只有一家时把它变成静态标签，但那样"OSL 为什么不能选"就无处可看了 ——
 * 而这恰恰是现在最常被问的问题。列表里灰掉一项并写明原因，
 * 比藏起来让人去翻文档有用。 */

async function onSelect(name) {
  const target = items.value.find((x) => x.name === name)
  if (!target || target.current) return
  if (!target.available) return ElMessage.error(target.blocked_reason || '该交易所当前不可用')

  if (risk.latched) {
    return ElMessage.error(
      `「${stateLabel(risk.botState)}」状态已锁定，须先人工解除才能切换交易所`,
    )
  }

  const warn = []
  if (risk.running) warn.push('机器人正在运行，切换后将停止')
  if (plan.approved) warn.push('已批准的盘前计划将失效，需按新盘口重新批准')
  if (c.hasOpenOrders) warn.push('当前交易所仍有活动挂单，切换会被拒绝，请先撤单')

  try {
    await ElMessageBox.confirm(
      `将切换到 ${target.label}（${target.default_symbol || '默认交易对'}）。` +
        (warn.length ? `\n\n${warn.map((w) => '· ' + w).join('\n')}` : ''),
      '切换交易所',
      {
        type: 'warning',
        confirmButtonText: '确认切换',
        cancelButtonText: '取消',
        // 换行靠 \n，需要保留原样
        customClass: 'pre-line-box',
      },
    )
  } catch {
    return
  }

  try {
    // 计划按「交易所+交易对」分别存放，切换后后端会给出新那家的当日计划
    // （没有就现生成一份未批准的），前端不需要再清什么
    const res = await c.switchExchange(name, target.default_symbol)
    // 状态在后端按「交易所+环境+交易对」各存一份，切过去自然是新那份，
    // 前端不需要（也不应该）去重置它，只补一条审计
    await risk.log('切换交易所', `${res.previous || '—'} → ${name}`)
    ElMessage.success(`已切换到 ${target.label}`)
  } catch (e) {
    ElMessage.error('切换失败：' + (e.message || e))
  }
}
</script>

<template>
  <el-dropdown
    trigger="click"
    :disabled="!c.connected || c.switching"
    @command="onSelect"
  >
    <span class="ex">
      <FaIcon :icon="['fas', 'building-columns']" />
      <b>{{ label }}</b>
      <FaIcon :icon="['fas', 'chevron-down']" class="caret" />
    </span>
    <template #dropdown>
      <el-dropdown-menu>
        <el-dropdown-item
          v-for="x in items"
          :key="x.name"
          :command="x.name"
          :disabled="!x.available || x.current"
        >
          <div class="ex-item">
            <div class="ex-row">
              <b>{{ x.label }}</b>
              <span v-if="x.current" class="ex-flag now">当前</span>
              <span v-else-if="!x.available" class="ex-flag off">不可用</span>
            </div>
            <small v-if="!x.available">{{ x.blocked_reason }}</small>
            <small v-else-if="!x.current">{{ x.default_symbol }}</small>
          </div>
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<style scoped>
.ex {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.1875rem 0.5rem;
  border: var(--hair) solid var(--line);
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  color: var(--text);
  cursor: pointer;
  white-space: nowrap;
}
.ex:hover { border-color: var(--amber); }
.ex > svg { font-size: 0.75rem; color: var(--muted); }
.ex .caret { font-size: 0.625rem; }

.ex-item { line-height: 1.4; padding: 0.125rem 0; }
.ex-row { display: flex; align-items: center; gap: 0.375rem; }
.ex-item small {
  display: block;
  font-size: 0.6875rem;
  color: var(--muted);
  max-width: 17rem;
  white-space: normal;
}
.ex-flag {
  font-size: 0.625rem;
  padding: 0 0.25rem;
  border-radius: var(--radius-sm);
  border: var(--hair) solid var(--line);
}
.ex-flag.now { color: var(--green); border-color: var(--green); }
.ex-flag.off { color: var(--muted); }
</style>
